import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import ProductReviews from '../components/ProductReviews';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  const fetchProduct = async () => {
    try {
      const response = await axiosInstance.get(`/products/${id}`);
      setProduct(response.data);
      if (response.data.variants.length > 0) {
        const first = response.data.variants[0];
        setSelectedVariant(first);
        setSelectedColor(first.color || null);
        setSelectedSize(first.size || null);
      }
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const colorOptions = useMemo(() => {
    if (!product) return [];
    return [...new Set(product.variants.map((variant) => variant.color).filter(Boolean))];
  }, [product]);

  const sizeOptions = useMemo(() => {
    if (!product) return [];
    return [...new Set(product.variants.map((variant) => variant.size).filter(Boolean))];
  }, [product]);

  const syncVariant = (color, size) => {
    if (!product) return;
    const match = product.variants.find(
      (variant) => (color ? variant.color === color : true) && (size ? variant.size === size : true)
    );
    if (match) {
      setSelectedVariant(match);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }
    try {
      setAdding(true);
      await axiosInstance.post('/cart/items', {
        product_id: product.id,
        variant_id: selectedVariant.id,
        quantity,
      });
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  // Not found / hidden from the shop: fetchProduct already redirected to /shop
  if (!product) return null;

  const price = product.discounted_price || product.base_price;
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  const addToCartVisible = Boolean(product);
  const addToCartPadding = addToCartVisible ? '16rem' : '8rem';

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] text-[#2B3A67]"
      style={{
        fontFamily: "'Outfit', sans-serif",
        paddingBottom: addToCartPadding,
        transition: 'padding-bottom 0.9s ease',
      }}
    >
      <header className="sticky top-0 z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-gray-100 pt-safe">
        <div className="flex items-center justify-between px-6 py-4">
          <button
            data-testid="back-button"
            onClick={() => navigate('/shop')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
          >
            <i className="ph-bold ph-arrow-left text-xl"></i>
          </button>
          <h1 className="font-extrabold text-lg" data-testid="product-title">{product.name}</h1>
          <button
            data-testid="cart-button"
            onClick={() => navigate('/cart')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
          >
            <i className="ph-bold ph-shopping-cart text-xl"></i>
          </button>
        </div>
      </header>

      <main className="px-6 pt-6 pb-24">
        <section className="bg-white rounded-3xl shadow-lg p-4 border border-gray-100">
          <div className="aspect-square rounded-2xl overflow-hidden mb-4">
            <img
              data-testid="product-image"
              src={product.images[selectedImage]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar">
            {product.images.map((image, index) => (
              <button
                key={image}
                onClick={() => setSelectedImage(index)}
                className={`w-20 h-20 rounded-2xl overflow-hidden border-2 ${
                  selectedImage === index ? 'border-[#E54562]' : 'border-transparent'
                }`}
                data-testid={`product-thumbnail-${index}`}
              >
                <img src={image} alt="Product" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </section>

        <section className="mt-6 space-y-4">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-bold" data-testid="product-brand">
            {product.brand}
          </p>
          <h2 className="text-2xl font-extrabold">{product.name}</h2>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-extrabold text-[#E54562]" data-testid="product-price">
              {formatCurrency(price)}
            </span>
            {product.discounted_price && (
              <span className="text-sm text-gray-400 line-through" data-testid="product-base-price">
                {formatCurrency(product.base_price)}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500" data-testid="product-description">
            {product.description}
          </p>
        </section>

        <section className="mt-6 space-y-4">
          {colorOptions.length > 0 && (
            <div>
              <h3 className="text-sm font-extrabold mb-3">Color</h3>
              <div className="flex gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    onClick={() => {
                      setSelectedColor(color);
                      syncVariant(color, selectedSize);
                    }}
                    className={`px-4 py-2 rounded-full text-xs font-bold border-2 ${
                      selectedColor === color ? 'border-[#E54562] text-[#E54562]' : 'border-gray-200 text-gray-500'
                    }`}
                    data-testid={`product-color-${color}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}
          {sizeOptions.length > 0 && (
            <div>
              <h3 className="text-sm font-extrabold mb-3">Size</h3>
              <div className="flex gap-2 flex-wrap">
                {sizeOptions.map((size) => (
                  <button
                    key={size}
                    onClick={() => {
                      setSelectedSize(size);
                      syncVariant(selectedColor, size);
                    }}
                    className={`px-4 py-2 rounded-full text-xs font-bold border-2 ${
                      selectedSize === size ? 'border-[#2B3A67] text-[#2B3A67]' : 'border-gray-200 text-gray-500'
                    }`}
                    data-testid={`product-size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="mt-6">
          <h3 className="text-sm font-extrabold mb-3">Quantity</h3>
          <div className="flex items-center gap-4">
            <button
              data-testid="decrease-quantity"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 rounded-full border-2 border-gray-200 font-bold"
            >
              -
            </button>
            <span data-testid="product-quantity" className="font-bold text-lg w-10 text-center">
              {quantity}
            </span>
            <button
              data-testid="increase-quantity"
              onClick={() => setQuantity(Math.min(selectedVariant?.stock || 1, quantity + 1))}
              disabled={!selectedVariant || quantity >= selectedVariant.stock}
              className="w-10 h-10 rounded-full border-2 border-gray-200 font-bold"
            >
              +
            </button>
          </div>
        </section>

        <ProductReviews productId={product.id} />
      </main>

      <div
        className={`fixed bottom-6 left-0 right-0 px-6 transition-all duration-1000 ease-out ${
          addToCartVisible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-24 opacity-0 pointer-events-none'
        }`}
        data-testid="product-cta-bar"
        aria-hidden={!addToCartVisible}
      >
        <div className="floating-bar flex items-center justify-center">
          <button
            data-testid="add-to-cart-button"
            onClick={handleAddToCart}
            disabled={adding || !selectedVariant || selectedVariant.stock === 0}
            className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg"
          >
            {adding ? 'Adding...' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
}